import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  DocumentIcon, 
  CpuChipIcon,
  ArrowRightOnRectangleIcon,
  ServerIcon,
  CubeIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  UserIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useServer } from '../contexts/ServerContext';

const MobileLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedServer, setSelectedServer, servers, loading, fetchServers, getCurrentServer, switchingServer } = useServer();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [serverDropdownOpen, setServerDropdownOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Backups', href: '/backups', icon: DocumentIcon },
    { name: 'Config Backup', href: '/backup-config', icon: Cog6ToothIcon },
    { name: 'Logs Backup', href: '/backup-logs', icon: DocumentTextIcon },
    { name: 'Configurações', href: '/global-config', icon: Cog6ToothIcon },
    { name: 'Sistema', href: '/system', icon: CpuChipIcon },
    { name: 'Docker', href: '/docker', icon: CubeIcon },
    { name: 'Logs Docker', href: '/docker-logs', icon: DocumentTextIcon },
    { name: 'Traefik', href: '/traefik', icon: GlobeAltIcon },
    { name: 'Servidores', href: '/servers', icon: ComputerDesktopIcon },
    { name: 'Usuários', href: '/users', icon: UserIcon },
  ];

  // Get current page name
  const currentPage = navigation.find(item => item.href === location.pathname)?.name || 'Dashboard';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* App Title */}
          <div className="flex items-center">
            <ServerIcon className="h-6 w-6 text-blue-600 mr-2" />
            <span className="font-bold text-gray-900">Manager</span>
          </div>

          {/* Server Status Indicator */}
          <div className="flex items-center">
            {switchingServer && (
              <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500 mr-2" />
            )}
            {getCurrentServer()?.connected !== false ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-500" />
            )}
          </div>
        </div>

        {/* Current Page & Server Selector */}
        <div className="px-4 pb-2 border-t border-gray-100">
          <div className="flex items-center justify-between py-2">
            <span className="text-sm font-medium text-gray-700">{currentPage}</span>
            <button
              onClick={() => setServerDropdownOpen(!serverDropdownOpen)}
              className="flex items-center text-sm text-gray-600"
            >
              <span className="truncate max-w-[150px]">{getCurrentServer()?.name || 'Selecione'}</span>
              <ChevronDownIcon className={`h-4 w-4 ml-1 transition-transform ${serverDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Server Dropdown */}
        {serverDropdownOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Servidor Ativo</h3>
                <button
                  onClick={() => {
                    fetchServers();
                    setServerDropdownOpen(false);
                  }}
                  className="p-1 text-gray-500"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="space-y-2">
                {servers.map((server) => (
                  <button
                    key={server.id}
                    onClick={() => {
                      setSelectedServer(server.id);
                      setServerDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                      selectedServer === server.id
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{server.name}</div>
                        <div className="text-xs text-gray-500">{server.host}</div>
                      </div>
                      {server.connected !== false ? (
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50" 
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            {/* Menu Header */}
            <div className="flex items-center justify-between px-4 py-4 bg-blue-600">
              <div className="flex items-center">
                <ServerIcon className="h-8 w-8 text-white" />
                <span className="ml-2 text-xl font-bold text-white">Server Manager</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 overflow-y-auto">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-3 mb-2 text-base font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  <item.icon className="h-6 w-6 mr-3" />
                  {item.name}
                </NavLink>
              ))}
            </nav>

            {/* Menu Footer */}
            <div className="px-4 py-4 border-t">
              <div className="mb-4">
                <p className="text-xs text-gray-500">Usuário logado:</p>
                <p className="text-sm font-medium text-gray-700 truncate">
                  {JSON.parse(localStorage.getItem('user') || '{}').phone || 'Usuário'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center justify-center w-full px-3 py-2 text-base font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
              >
                <ArrowRightOnRectangleIcon className="h-6 w-6 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar (unchanged) */}
      <div className="hidden lg:flex lg:fixed lg:inset-y-0 lg:w-64 lg:flex-col">
        {/* Original desktop sidebar content */}
        <div className="flex flex-col flex-1 bg-white shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-center h-16 px-4 bg-blue-600">
            <div className="flex items-center">
              <ServerIcon className="h-8 w-8 text-white" />
              <span className="ml-2 text-xl font-bold text-white">Server Manager</span>
            </div>
          </div>

          {/* Server Selector */}
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Servidor Ativo:</span>
                <button
                  onClick={fetchServers}
                  disabled={loading}
                  className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-50"
                  title="Atualizar lista"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              <div className="space-y-2">
                <select
                  value={selectedServer}
                  onChange={(e) => setSelectedServer(e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {servers.map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.name}
                    </option>
                  ))}
                </select>
                
                {getCurrentServer() && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 truncate">
                      {getCurrentServer().host}
                    </span>
                    {getCurrentServer().connected !== false ? (
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Online
                      </span>
                    ) : (
                      <span className="flex items-center text-red-600">
                        <XCircleIcon className="h-3 w-3 mr-1" />
                        Offline
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div className="px-4 py-4 border-t">
            <button
              onClick={handleLogout}
              className="flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        <main className="pt-20 lg:pt-0 px-4 py-6 lg:px-8">
          {switchingServer && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg flex items-center text-blue-700">
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
              Trocando servidor...
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default MobileLayout;